/**
 * Podcast filtering utility for KaiOS app
 * Implements the same filtering logic as the backend KaiOS app
 */

/**
 * Get blacklisted domains from environment variable
 */
const getBlacklistedDomains = () => {
  const domains = process.env.REACT_APP_BLACKLISTED_DOMAINS || '';
  return domains.split(',').map(domain => domain.trim()).filter(domain => domain.length > 0);
};

/**
 * Check if a URL contains any blacklisted domain
 */
const isUrlBlacklisted = (url) => {
  if (!url || typeof url !== 'string') return true;
  
  const blacklistedDomains = getBlacklistedDomains();
  const urlLower = url.toLowerCase();
  
  return blacklistedDomains.some(domain => {
    const domainLower = domain.toLowerCase();
    return urlLower.includes(domainLower);
  });
};

/**
 * Check if a podcast's RSS URL is blacklisted
 * Handles both KaiOS backend format and MusicEngine format
 */
const isPodcastRSSBlacklisted = (podcast) => {
  if (!podcast || typeof podcast !== 'object') return true;
  
  // For MusicEngine format, check different field names
  // MusicEngine uses 'rss_feed_url' as the field name
  const rssURL = podcast.rssURL || podcast.rss_url || podcast.rss_feed_url || podcast.rss || podcast.RSS;
  
  return isUrlBlacklisted(rssURL);
};

/**
 * Check if a podcast has all required fields
 * Handles both KaiOS backend format and MusicEngine format
 */
const hasRequiredFields = (podcast) => {
  if (!podcast || typeof podcast !== 'object') return false;
  
  // For MusicEngine format, check different field names
  const title = podcast.title || podcast.name;
  const rssURL = podcast.rssURL || podcast.rss_url || podcast.rss_feed_url || podcast.rss || podcast.RSS;
  const author = podcast.author || (podcast.artist && podcast.artist.name) || podcast.creator || podcast.artist_name;
  const description = podcast.description || podcast.summary;
  
  // Check required fields as per KaiOS backend logic
  const requiredFields = [
    { value: title, name: 'title' },
    { value: rssURL, name: 'rssURL' },
    { value: author, name: 'author' },
    { value: description, name: 'description' }
  ];
  
  return requiredFields.every(field => {
    return field.value && 
           typeof field.value === 'string' && 
           field.value.trim().length > 0;
  });
};

/**
 * Check if a podcast is deleted
 */
const isPodcastDeleted = (podcast) => {
  if (!podcast || typeof podcast !== 'object') return true;
  
  // Check isDeleted flag
  return Boolean(podcast.isDeleted);
};

/**
 * Main podcast filtering function
 * Implements the same logic as KaiOS backend:
 * - Skip deleted podcasts (isDeleted: false)
 * - Skip podcasts from blacklisted domains
 * - Ensure podcasts have valid title, rssURL, author, and description
 */
export const filterPodcasts = (podcasts) => {
  if (!Array.isArray(podcasts)) {
    return [];
  }
  
  const filteredPodcasts = podcasts.filter(podcast => {
    // Skip deleted podcasts
    if (isPodcastDeleted(podcast)) {
      return false;
    }
    
    // Skip podcasts from blacklisted domains
    if (isPodcastRSSBlacklisted(podcast)) {
      return false;
    }
    
    // Skip podcasts missing required fields
    if (!hasRequiredFields(podcast)) {
      return false;
    }
    
    return true;
  });
  
  return filteredPodcasts;
};

/**
 * Filter podcasts from MusicEngine API response
 * Handles different response formats from MusicEngine
 */
export const filterMusicEnginePodcasts = (response) => {
  if (!response) return response;
  
  let podcasts = [];
  let responseData = response;
  
  // Handle different response formats
  if (Array.isArray(response)) {
    podcasts = response;
    responseData = response;
  } else if (response.data && Array.isArray(response.data)) {
    podcasts = response.data;
    responseData = response.data;
  } else if (response.podcasts && Array.isArray(response.podcasts)) {
    podcasts = response.podcasts;
    responseData = response.podcasts;
  } else if (response.browse && response.browse.podcasts && Array.isArray(response.browse.podcasts.data)) {
    // Handle browse response format
    podcasts = response.browse.podcasts.data;
    responseData = {
      ...response,
      browse: {
        ...response.browse,
        podcasts: {
          ...response.browse.podcasts,
          data: filterPodcasts(podcasts)
        }
      }
    };
    return responseData;
  }
  
  const filteredPodcasts = filterPodcasts(podcasts);
  
  // If response was an array, return filtered array
  if (Array.isArray(response)) {
    return filteredPodcasts;
  }
  
  // Otherwise, return response with filtered data
  if (response.data && Array.isArray(response.data)) {
    return {
      ...response,
      data: filteredPodcasts
    };
  }
  
  if (response.podcasts && Array.isArray(response.podcasts)) {
    return {
      ...response,
      podcasts: filteredPodcasts
    };
  }
  
  return response;
};

/**
 * Filter podcasts from channels/trending response
 */
export const filterTrendingChannels = (channels) => {
  if (!Array.isArray(channels)) {
    return [];
  }
  
  return channels.map(channel => {
    if (channel.objects && channel.objects.data) {
      const filteredPodcasts = filterPodcasts(channel.objects.data);
      
      return {
        ...channel,
        objects: {
          ...channel.objects,
          data: filteredPodcasts
        }
      };
    }
    return channel;
  });
};

/**
 * Debug function to log filtering statistics
 */
export const logFilteringStats = (originalCount, filteredCount, source = 'unknown') => {
  const filteredOut = originalCount - filteredCount;
  const percentage = originalCount > 0 ? ((filteredOut / originalCount) * 100).toFixed(1) : 0;
};

export default {
  filterPodcasts,
  filterMusicEnginePodcasts,
  filterTrendingChannels,
  logFilteringStats,
  isUrlBlacklisted,
  isPodcastRSSBlacklisted,
  hasRequiredFields,
  isPodcastDeleted
};
